import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { PaddingOrCallback, VictoryAxisProps } from 'victory';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryClipContainer,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { AreaGradient } from './components/AreaGradient';
import { DataLabel } from './components/DataLabel/DataLabel';
import { LineGraphFlyout } from './components/LineGraphFlyout/LineGraphFlyout';
import { VictoryWmgTheme } from './components/VictoryWmgTheme';

export type LineGraphData = {
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}[];

export type StreamType =
  | 'streams'
  | 'organicStreams'
  | 'programmedStreams'
  | 'benchmarkStreams';

interface LineGraphProps {
  streamType?: StreamType;
  data: LineGraphData[];
  lineColors?: string[];
  containerClassName?: string;
  xAxisProps?: VictoryAxisProps;
  yAxisProps?: VictoryAxisProps;
  tooltip?: ReactElement | undefined;
  tooltipPadding?: PaddingOrCallback;
  tooltipOffset?: number[];
  targetPercentage?: number;
  dataLabelText?: string;
}

const MAX_CHART_HEIGHT = 114;
const MIN_CHART_HEIGHT = 80;

const TRANSITION_START_WIDTH = 1512;
const TRANSITION_END_WIDTH = 1920;

const getCalculatedChartHeight = () => {
  const currentWidth = window.innerWidth;

  if (currentWidth <= TRANSITION_START_WIDTH) {
    return MAX_CHART_HEIGHT;
  }

  if (currentWidth >= TRANSITION_END_WIDTH) {
    return MIN_CHART_HEIGHT;
  }

  const transitionRange = TRANSITION_END_WIDTH - TRANSITION_START_WIDTH;
  const currentPosition = currentWidth - TRANSITION_START_WIDTH;
  const percentage = currentPosition / transitionRange;

  const heightRange = MIN_CHART_HEIGHT - MAX_CHART_HEIGHT;
  const newHeight = MAX_CHART_HEIGHT + heightRange * percentage;

  return newHeight;
};

function LineGraph({
  data,
  streamType,
  targetPercentage = 100,
  containerClassName = '',
  lineColors = ['#000000'],
  xAxisProps = {},
  yAxisProps = {},
  tooltip = undefined,
  tooltipPadding = 12,
  tooltipOffset = [0, 0],
  dataLabelText = 'Label',
}: LineGraphProps) {
  const [hovered, setHovered] = useState(false);
  const areaGradientIdPrefix = 'areaGradient_';

  const [chartHeight, setChartHeight] = useState(getCalculatedChartHeight);

  const isBenchmarkStream = streamType === 'benchmarkStreams';

  useEffect(() => {
    const handleResize = () => {
      setChartHeight(getCalculatedChartHeight());
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={containerClassName}>
      {lineColors.map((color, index) => (
        <AreaGradient
          key={index}
          id={areaGradientIdPrefix + index}
          lineColor={color}
        />
      ))}

      <VictoryChart
        padding={{
          top: 3,
          left: yAxisProps?.orientation === 'left' ? 14 : 0,
          right: 5,
          bottom: 15,
        }}
        height={chartHeight}
        domainPadding={[0, 0]}
        domain={{ y: [-5, 100] }}
        theme={VictoryWmgTheme}
        containerComponent={
          isBenchmarkStream ? undefined : (
            <VictoryVoronoiContainer
              events={{
                onMouseOver: () => setHovered(true),
                onMouseOut: () => setHovered(false),
              }}
            />
          )
        }
      >
        {isBenchmarkStream ? (
          <DataLabel
            x={1}
            y={data[0][data[0].length - 1].y}
            text={[dataLabelText, `${targetPercentage}%`]}
          />
        ) : null}

        <VictoryAxis crossAxis {...xAxisProps} />
        <VictoryAxis dependentAxis {...yAxisProps} />

        <VictoryAxis
          orientation="top"
          style={{
            grid: { stroke: 'transparent' },
            ticks: { size: 0 },
            tickLabels: { fill: 'transparent' },
          }}
        />

        {/* Right Border */}
        <VictoryAxis
          dependentAxis
          orientation="right"
          style={{
            ticks: { size: 0 },
            tickLabels: { fill: 'transparent' },
          }}
        />

        {data.map((dataPoint, index) => {
          let dataType;

          if (streamType === 'organicStreams') {
            dataType = index === 0 ? 'library' : 'search';
          } else {
            dataType = 'streams';
          }

          return (
            <VictoryArea
              groupComponent={
                <VictoryClipContainer clipPadding={{ top: 6, right: 6 }} />
              }
              key={index}
              labelComponent={
                <VictoryTooltip
                  orientation="left"
                  flyoutComponent={
                    <LineGraphFlyout
                      streamType={streamType}
                      dataType={dataType}
                      data={data}
                      lineColors={lineColors}
                      customFlyoutComponent={tooltip}
                      customFlyoutOffset={tooltipOffset}
                    />
                  }
                  flyoutPadding={tooltipPadding}
                />
              }
              labels={({ datum }) => datum.label ?? ''}
              interpolation="monotoneX"
              data={dataPoint}
              style={{
                data: {
                  stroke: lineColors[index],
                  strokeWidth: 0.8,
                  fill: `url(#${areaGradientIdPrefix + index})`,
                  fillOpacity: hovered ? 0 : 0,
                  strokeDasharray:
                    isBenchmarkStream && index === 1 ? '2' : 'none',
                },
              }}
            />
          );
        })}
      </VictoryChart>
    </div>
  );
}

LineGraph.displayName = 'LineGraph';
export { LineGraph };
